<template>
    <div>
        <BasicHeader pretitle="PAICE" title="Settings" buttonText="Refresh" buttonClass="btn-primary"
            buttonIconClass="fa fa-rotate-right me-1" :buttonLoading="refreshing" :buttonDisabled="refreshDisabled"
            @btnClick="refresh" />

        <div class="container-fluid">
            <div class="row settings-layout">
                <!-- Tab rail -->
                <div class="col-12 col-lg-4 col-xl-3">
                    <nav class="settings-nav" aria-label="Settings sections">
                        <p class="settings-nav-label">Configuration</p>
                        <button v-for="tab in tabs" :key="tab.name" type="button" class="settings-nav-item"
                            :class="{ active: activeTab === tab.name }" @click="selectTab(tab.name)">
                            <span class="settings-nav-icon">
                                <i class="fa" :class="`fa-${tab.icon}`"></i>
                            </span>
                            <span class="settings-nav-text">
                                <span class="settings-nav-title">
                                    {{ tab.label }}
                                    <span class="settings-nav-badge" v-if="tab.badge">{{ tab.badge }}</span>
                                    <span class="settings-nav-dot" v-if="tabIsDirty(tab.name)"
                                        title="Unsaved changes"></span>
                                </span>
                                <span class="settings-nav-desc">{{ tab.description }}</span>
                            </span>
                            <i class="fa fa-chevron-right settings-nav-chevron"></i>
                        </button>
                    </nav>
                </div>

                <!-- Panel -->
                <div class="col-12 col-lg-8 col-xl-9">
                    <!-- Agent settings -->
                    <div v-if="activeTab === 'agent'">
                        <div class="card">
                            <div class="card-header">
                                <div>
                                    <h4 class="card-header-title">Agent Settings</h4>
                                    <p class="settings-subtitle mb-0">
                                        Applies to every PAICE chat for {{ cid }}.
                                    </p>
                                </div>
                                <div class="d-flex align-items-center gap-2">
                                    <span class="text-muted updated-meta" v-if="settings?.updatedAt">
                                        Updated {{ DateTimeFormat(settings.updatedAt) }}<template
                                            v-if="settings.updatedBy"> by {{ settings.updatedBy }}</template>
                                    </span>
                                </div>
                            </div>

                            <div class="card-body">
                                <div v-if="loading" class="settings-loading">
                                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                                        <span class="visually-hidden">Loading...</span>
                                    </div>
                                    <span>Loading settings…</span>
                                </div>

                                <div v-else-if="loadError"
                                    class="text-center card bg-light mb-0 p-3 border border-dashed">
                                    <span>
                                        <p class="text-muted mb-0">
                                            <i class="fa-solid fa-circle-exclamation me-1"></i>
                                            {{ loadError }}
                                        </p>
                                    </span>
                                </div>

                                <template v-else>
                                    <!-- System prompt -->
                                    <section class="settings-field">
                                        <div class="settings-field-head">
                                            <div>
                                                <label class="form-label mb-0" for="paice-system-prompt">System
                                                    prompt</label>
                                                <p class="settings-help mb-0">
                                                    Appended to the base system prompt on every chat. Use it for tone,
                                                    house terminology,
                                                    or defaults the assistant should assume.
                                                </p>
                                            </div>
                                            <span class="source-pill" :class="promptSourceClass">
                                                {{ draft.systemPrompt.trim() ? 'Customized' : 'Service default' }}
                                            </span>
                                        </div>

                                        <div class="editor">
                                            <div class="editor-toolbar">
                                                <div class="editor-modes" role="tablist">
                                                    <button type="button" class="editor-mode"
                                                        :class="{ active: !previewPrompt }"
                                                        @click="previewPrompt = false" role="tab"
                                                        :aria-selected="!previewPrompt">
                                                        <i class="fa fa-pen"></i> Write
                                                    </button>
                                                    <button type="button" class="editor-mode"
                                                        :class="{ active: previewPrompt }" @click="previewPrompt = true"
                                                        role="tab" :aria-selected="previewPrompt">
                                                        <i class="fa fa-eye"></i> Preview
                                                    </button>
                                                </div>
                                                <span class="editor-count" :class="{ 'text-danger': overPromptLimit }">
                                                    {{ promptLength.toLocaleString() }} / {{
                                                        maxPromptChars.toLocaleString() }}
                                                </span>
                                            </div>

                                            <textarea v-show="!previewPrompt" id="paice-system-prompt"
                                                :disabled="!canEditSettings" v-model="draft.systemPrompt"
                                                class="form-control editor-input" rows="12"
                                                placeholder="e.g. Always answer in metric units. When summarizing a table, lead with the three largest movers."></textarea>

                                            <div v-show="previewPrompt" class="editor-preview">
                                                <Markdown v-if="draft.systemPrompt.trim()"
                                                    :content="draft.systemPrompt" />
                                                <p v-else class="editor-preview-empty mb-0">
                                                    Nothing to preview. Add instructions in the Write tab.
                                                </p>
                                            </div>
                                        </div>

                                        <p v-if="overPromptLimit" class="text-danger settings-help mt-2 mb-0">
                                            Trim {{ (promptLength - maxPromptChars).toLocaleString() }} characters to
                                            save.
                                        </p>
                                    </section>

                                    <hr class="settings-divider" />

                                    <!-- Model -->
                                    <section class="settings-field">
                                        <div class="settings-field-head">
                                            <div>
                                                <label class="form-label mb-0">Model</label>
                                                <p class="settings-help mb-0">
                                                    Used for chat responses. Any model id is accepted — an id this
                                                    account cannot invoke
                                                    fails when a chat runs, not when you save.
                                                </p>
                                            </div>
                                            <span class="source-pill" :class="modelSourceClass">
                                                {{ draft.modelId ? 'Customized' : 'Service default' }}
                                            </span>
                                        </div>

                                        <div class="row g-3 align-items-start">
                                            <div class="col-12 col-xl-7">
                                                <ModelSelect v-model="draft.modelId" :models="availableModels"
                                                    :disabled="!canEditSettings" :catalog-source="modelCatalogSource"
                                                    :region="modelCatalogRegion" />
                                                <button v-if="draft.modelId" type="button"
                                                    class="btn btn-link btn-sm ps-0 revert-link"
                                                    @click="draft.modelId = ''">
                                                    Revert to service default
                                                </button>
                                            </div>
                                            <div class="col-12 col-xl-5">
                                                <div class="resolved-box">
                                                    <span class="resolved-label">Chats will use</span>
                                                    <span class="resolved-value">{{ effectiveModelId || '—' }}</span>
                                                    <span class="resolved-note" v-if="!draft.modelId">
                                                        Inherited from the service default.
                                                    </span>
                                                    <!-- ModelSelect only knows the override, so an
                                                         advisory on the inherited default would go
                                                         unsaid. It still applies to every chat. -->
                                                    <span class="resolved-advisory" v-if="inheritedAdvisory">
                                                        <i class="fa fa-triangle-exclamation me-1"></i>
                                                        {{ inheritedAdvisory }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                </template>
                            </div>

                            <div class="card-footer" v-if="!loading && !loadError">
                                <div class="footer-status">
                                    <span v-if="!canEditSettings" class="text-muted">
                                        <i class="fa fa-lock me-1"></i>
                                        Read only — only admins can edit settings.
                                    </span>
                                    <span v-else-if="saveError" class="text-danger">
                                        <i class="fa fa-circle-exclamation me-1"></i>{{ saveError }}
                                    </span>
                                    <span v-else-if="isDirty" class="text-muted">Unsaved changes</span>
                                    <span v-else class="text-muted">All changes saved</span>
                                </div>
                                <div class="d-flex gap-2">
                                    <button class="btn btn-outline-danger btn-sm" v-if="hasStoredSettings"
                                        :disabled="saving || !canEditSettings" @click="resetAll">
                                        Reset to defaults
                                    </button>
                                    <button class="btn btn-outline-secondary btn-sm" :disabled="!isDirty || saving"
                                        @click="discard">
                                        Discard
                                    </button>
                                    <button class="btn btn-primary btn-sm" :disabled="!canSaveAgentSetting"
                                        @click="save()">
                                        <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status"
                                            style="width:1em; height:1em">
                                            <span class="visually-hidden">Saving...</span>
                                        </span>
                                        Save changes
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Context layer -->
                    <div v-else-if="activeTab === 'context'">
                        <div class="card">
                            <div class="card-header">
                                <div>
                                    <h4 class="card-header-title">Context Layer</h4>
                                    <p class="settings-subtitle mb-0">
                                        Describes {{ cid }}'s own datasets, metrics and rules so PAICE answers
                                        from your data rather than generic knowledge.
                                    </p>
                                </div>
                                <div class="d-flex align-items-center gap-2">
                                    <span class="source-pill"
                                        :class="hasStoredContext ? 'source-pill-custom' : 'source-pill-default'">
                                        {{ hasStoredContext ? 'Configured' : 'Not configured' }}
                                    </span>
                                </div>
                            </div>

                            <div class="card-body">
                                <div v-if="contextLoading" class="settings-loading">
                                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                                        <span class="visually-hidden">Loading...</span>
                                    </div>
                                    <span>Loading context layer…</span>
                                </div>

                                <div v-else-if="contextLoadError"
                                    class="text-center card bg-light mb-0 p-3 border border-dashed">
                                    <span>
                                        <p class="text-muted mb-0">
                                            <i class="fa-solid fa-circle-exclamation me-1"></i>
                                            {{ contextLoadError }}
                                        </p>
                                    </span>
                                </div>

                                <!-- Nothing authored yet: offer a starting point rather than
                                     an empty editor the user has to guess the shape of. -->
                                <div v-else-if="showContextIntro" class="context-intro">
                                    <i class="fa fa-layer-group context-intro-icon"></i>
                                    <h4 class="context-intro-title">No context layer yet</h4>
                                    <p class="context-intro-text">
                                        Without one, PAICE can only reason about your data generically. Describe your
                                        domains, datasets, fields and metrics so it can answer in your terms — your
                                        table names, your business language, your joins — and add rules for the
                                        constraints it must never break.
                                    </p>
                                    <div v-if="canEditSettings" class="context-intro-actions">
                                        <button class="btn btn-primary btn-sm" @click="startFromTemplate">
                                            <i class="fa fa-wand-magic-sparkles me-1"></i> Start from a template
                                        </button>
                                        <button class="btn btn-white btn-sm" @click="startBlank">
                                            Start from scratch
                                        </button>
                                    </div>
                                    <p v-else class="context-intro-text mb-0">
                                        <i class="fa fa-lock me-1"></i>
                                        Only admins can author the context layer.
                                    </p>
                                </div>

                                <template v-else>
                                    <!-- Mode switch + document stats -->
                                    <div class="context-bar">
                                        <div class="editor-modes" role="tablist">
                                            <button type="button" class="editor-mode"
                                                :class="{ active: contextMode === 'yaml' }" role="tab"
                                                :aria-selected="contextMode === 'yaml'" @click="switchMode('yaml')">
                                                <i class="fa fa-code"></i> YAML
                                            </button>
                                            <button type="button" class="editor-mode"
                                                :class="{ active: contextMode === 'easy' }" role="tab"
                                                :aria-selected="contextMode === 'easy'" @click="switchMode('easy')">
                                                <i class="fa fa-rectangle-list"></i> Easy mode
                                            </button>
                                        </div>

                                        <div class="context-stats">
                                            <span><strong>{{ contextStats.domains }}</strong> domains</span>
                                            <span><strong>{{ contextStats.datasets }}</strong> datasets</span>
                                            <span><strong>{{ contextStats.fields }}</strong> fields</span>
                                            <span><strong>{{ contextStats.metrics }}</strong> metrics</span>
                                            <span><strong>{{ contextStats.rules }}</strong> rules</span>
                                            <span class="context-size" :class="{ 'text-danger': overContextLimit }">
                                                {{ formattedContextSize }}
                                            </span>
                                        </div>
                                    </div>

                                    <!-- YAML mode -->
                                    <div v-show="contextMode === 'yaml'" class="context-editor">
                                        <CodemirrorEditor editorId="paiceContextEditor" mode="yaml"
                                            v-model="contextDraft" :performLint="true" :lineWrapping="true"
                                            :readOnly="!canEditSettings" customHeight="60vh" />
                                    </div>

                                    <!-- Easy mode. An incomplete document still renders — the
                                         form is where those gaps get filled in. Only YAML that
                                         does not parse has no object to bind to. -->
                                    <template v-if="contextMode === 'easy'">
                                        <ContextLayerBuilder v-if="contextDoc" :modelValue="contextDoc"
                                            :editable="canEditSettings" :errors="contextErrors"
                                            @update:modelValue="onBuilderChange" />
                                        <div v-else class="context-unparseable">
                                            <i class="fa fa-file-circle-xmark"></i>
                                            <p class="mb-1"><strong>This document can't be opened as a form.</strong>
                                            </p>
                                            <p class="mb-3">
                                                The YAML has a syntax error, so there is no structure to lay out. Fix
                                                it in YAML mode and Easy mode will pick it up.
                                            </p>
                                            <button type="button" class="btn btn-sm btn-white"
                                                @click="switchMode('yaml')">
                                                Go to YAML
                                            </button>
                                        </div>
                                    </template>

                                    <!-- Validation status sits under the editor: it reports on
                                         what is above it, and in Easy mode the offending field
                                         is usually on screen next to it. -->
                                    <div v-if="contextErrors.length" class="context-alert error">
                                        <i class="fa fa-circle-exclamation mt-1"></i>
                                        <div class="flex-grow-1">
                                            <strong>
                                                {{ contextErrors.length }}
                                                {{ contextErrors.length === 1 ? 'issue' : 'issues' }} to fix before
                                                saving
                                            </strong>
                                            <ul class="context-error-list">
                                                <li v-for="(err, i) in visibleContextErrors" :key="i">
                                                    <code v-if="err.path">{{ err.path }}</code>
                                                    <span>{{ err.message }}</span>
                                                </li>
                                            </ul>
                                            <button v-if="contextErrors.length > errorPreviewCount" type="button"
                                                class="btn btn-link btn-sm p-0 context-more"
                                                @click="showAllErrors = !showAllErrors">
                                                {{ showAllErrors
                                                    ? 'Show fewer'
                                                    : `Show ${contextErrors.length - errorPreviewCount} more` }}
                                            </button>
                                        </div>
                                    </div>

                                    <div v-else-if="contextDirty" class="context-alert ok">
                                        <i class="fa fa-circle-check mt-1"></i>
                                        <span>Valid against the context layer schema.</span>
                                    </div>
                                </template>
                            </div>

                            <div class="card-footer" v-if="!contextLoading && !contextLoadError && !showContextIntro">
                                <div class="footer-status">
                                    <span v-if="!canEditSettings" class="text-muted">
                                        <i class="fa fa-lock me-1"></i>
                                        Read only — only admins can edit settings.
                                    </span>
                                    <span v-else-if="contextSaveError" class="text-danger">
                                        <i class="fa fa-circle-exclamation me-1"></i>{{ contextSaveError }}
                                    </span>
                                    <span v-else-if="overContextLimit" class="text-danger">
                                        <i class="fa fa-circle-exclamation me-1"></i>
                                        Too large — the limit is {{ maxContextMB }} MB.
                                    </span>
                                    <span v-else-if="contextDirty" class="text-muted">Unsaved changes</span>
                                    <span v-else class="text-muted">All changes saved</span>
                                </div>
                                <div class="d-flex gap-2">
                                    <button class="btn btn-outline-secondary btn-sm"
                                        :disabled="!contextDirty || contextSaving" @click="discardContext">
                                        Discard
                                    </button>
                                    <button class="btn btn-primary btn-sm" :disabled="!canSaveContext"
                                        @click="saveContext">
                                        <span v-if="contextSaving" class="spinner-border spinner-border-sm me-1"
                                            role="status" style="width:1em; height:1em">
                                            <span class="visually-hidden">Saving...</span>
                                        </span>
                                        Save context
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- MCP servers -->
                    <McpServers v-if="activeTab === 'servers'" :reload-token="serversReloadToken"
                        @update:loading="serversLoading = $event" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import BasicHeader from '@/components/BasicHeader.vue';
import Markdown from '@/components/Markdown.vue';
import ModelSelect from '@/components/paice/ModelSelect.vue';
import CodemirrorEditor from '@/components/CodemirrorEditor.vue';
import ContextLayerBuilder from '@/components/paice/ContextLayerBuilder.vue';
import McpServers from '@/components/paice/McpServers.vue';
import { DateTimeFormat } from '@/utils/commonFunction';
import {
    parseAndValidate, stringifyContextLayer, stringifyPreservingComments, normalizeServerErrors,
    summarize, byteLength, blankLayer, STARTER_TEMPLATE, MAX_CONTEXT_BYTES,
} from '@/utils/contextLayer';

// Mirrors PAICE_MAX_CUSTOM_PROMPT_CHARS on the agents service.
const MAX_PROMPT_CHARS = 20000;

// How many validation errors to list before collapsing behind "show more".
const ERROR_PREVIEW_COUNT = 5;

// The `tab` query param is the page's address: it makes a section shareable and
// survives a reload. `agent` is the default, and is written into the URL on
// arrival so a bare /paice/settings link normalizes to an explicit one.
const TABS = [
    {
        name: 'agent',
        label: 'Agent Settings',
        icon: 'robot',
        description: 'System prompt and model',
    },
    {
        name: 'context',
        label: 'Context Layer',
        icon: 'layer-group',
        description: 'Rules, datasets and metrics',
    },
    {
        name: 'servers',
        label: 'MCP Servers',
        icon: 'plug',
        description: 'Tools from outside Aqfer',
        badge: 'Beta',
    },
];
const DEFAULT_TAB = TABS[0].name;

export default {
    name: "PaiceSettings",
    components: { BasicHeader, Markdown, ModelSelect, CodemirrorEditor, ContextLayerBuilder, McpServers },
    data() {
        return {
            tabs: TABS,
            // Each tab loads on first visit rather than on page load, so landing
            // on one section does not spend a request on the other.
            loadedTabs: {},
            loading: false,
            loadError: "",
            saving: false,
            saveError: "",
            previewPrompt: false,
            maxPromptChars: MAX_PROMPT_CHARS,
            // What the form shows. Empty string means "no override" for both
            // fields, which maps to null in the request body.
            draft: { systemPrompt: "", modelId: "" },
            // Snapshot of the last server state, for dirty-checking and discard.
            baseline: { systemPrompt: "", modelId: "" },

            // --- Context layer ---
            contextLoading: false,
            contextLoadError: "",
            contextSaving: false,
            contextSaveError: "",
            contextMode: 'yaml',
            // The document as text. This is the single source of truth: Easy
            // mode edits are serialized straight back into it, so the two views
            // can never hold different documents.
            contextDraft: "",
            // Last saved text, for dirty-checking and discard.
            contextBaseline: "",
            // Parsed form of the draft, handed to the builder. Rebuilt on every
            // draft change, and null while the YAML does not parse.
            contextDoc: null,
            contextErrors: [],
            contextSyntaxError: false,
            showAllErrors: false,
            errorPreviewCount: ERROR_PREVIEW_COUNT,
            maxContextBytes: MAX_CONTEXT_BYTES,
            // Set once the user picks a starting point on the empty state, so
            // the intro does not reappear while they are authoring.
            contextStarted: false,

            // --- MCP servers ---
            // The panel owns its own fetching and has nothing unsaved to warn
            // about, so the page only tells it to reload and mirrors the
            // resulting loading state onto the Refresh button.
            serversReloadToken: 0,
            serversLoading: false,
        };
    },
    computed: {
        cid() {
            return this.$store.state.currentClient?.cid;
        },
        /**
         * The URL is the source of truth for which section is open, so a shared
         * link lands on the same one. An unknown or missing `tab` falls back to
         * the default rather than rendering nothing.
         */
        activeTab() {
            const requested = this.$route.query.tab;
            return TABS.some((t) => t.name === requested) ? requested : DEFAULT_TAB;
        },
        /** Refresh acts on the section in view, so its state tracks that tab. */
        refreshing() {
            if (this.activeTab === 'servers') return this.serversLoading;
            return this.activeTab === 'context' ? this.contextLoading : this.loading;
        },
        refreshDisabled() {
            if (this.activeTab === 'servers') return this.serversLoading;
            return this.activeTab === 'context'
                ? this.contextLoading || this.contextSaving
                : this.loading || this.saving;
        },
        settings() {
            return this.$store.state.paice.paiceSettings;
        },
        availableModels() {
            return this.$store.state.paice.availableModels;
        },
        modelCatalogSource() {
            return this.$store.state.paice.modelCatalogSource;
        },
        modelCatalogRegion() {
            return this.$store.state.paice.modelCatalogRegion;
        },
        /** True once the client has its own stored record (so DELETE is meaningful). */
        hasStoredSettings() {
            return !!this.settings?.updatedAt;
        },
        /** The service default, i.e. what modelId resolves to with no override. */
        defaultModelId() {
            const flagged = this.availableModels.find((m) => m.default);
            if (flagged) return flagged.id;
            // Before the catalog loads, a GET whose source is 'default' still
            // tells us the resolved id.
            return this.settings?.source?.modelId === 'default' ? this.settings.modelId : "";
        },
        effectiveModelId() {
            return this.draft.modelId || this.defaultModelId;
        },
        /**
         * The advisory for a model that is in effect only because nothing was
         * overridden. When there *is* an override the picker below shows its own
         * advisory, so this stays quiet rather than saying it twice.
         */
        inheritedAdvisory() {
            if (this.draft.modelId || !this.effectiveModelId) return "";
            const model = this.availableModels.find((m) => m.id === this.effectiveModelId);
            return model?.advisory || "";
        },
        promptLength() {
            return this.draft.systemPrompt.trim().length;
        },
        overPromptLimit() {
            return this.promptLength > this.maxPromptChars;
        },
        isDirty() {
            return (
                this.draft.systemPrompt.trim() !== this.baseline.systemPrompt.trim() ||
                this.draft.modelId !== this.baseline.modelId
            );
        },
        promptSourceClass() {
            return this.draft.systemPrompt.trim() ? 'source-pill-custom' : 'source-pill-default';
        },
        modelSourceClass() {
            return this.draft.modelId ? 'source-pill-custom' : 'source-pill-default';
        },
        canSaveAgentSetting() {
            return this.isDirty
                && !this.saving
                && !this.overPromptLimit
                && this.canEditSettings;
        },

        // --- Context layer ---
        /** The stored layer, or "" once we know this cid has never authored one. */
        storedContext() {
            return this.$store.state.paice.contextLayer;
        },
        hasStoredContext() {
            return !!(this.storedContext && this.storedContext.trim());
        },
        /**
         * Show the "no context layer yet" pitch instead of an empty editor —
         * but only until the user picks a starting point.
         */
        showContextIntro() {
            return !this.hasStoredContext && !this.contextStarted && !this.contextDraft.trim();
        },
        contextDirty() {
            return this.contextDraft !== this.contextBaseline;
        },
        contextStats() {
            return summarize(this.contextDoc);
        },
        contextBytes() {
            return byteLength(this.contextDraft);
        },
        overContextLimit() {
            return this.contextBytes > this.maxContextBytes;
        },
        maxContextMB() {
            return Math.round(this.maxContextBytes / (1024 * 1024));
        },
        formattedContextSize() {
            const kb = this.contextBytes / 1024;
            return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(2)} MB`;
        },
        visibleContextErrors() {
            return this.showAllErrors
                ? this.contextErrors
                : this.contextErrors.slice(0, this.errorPreviewCount);
        },
        canEditSettings() {
            // only priviledged roles can edit any settings
            const allowed = this.$store.state.paice.privilegedRoles || [];
            return this.$store.getters.userRoles.isAccessByRole(allowed);
        },
        canSaveContext() {
            return this.contextDirty
                && !this.contextSaving
                && !this.contextErrors.length
                && !this.overContextLimit
                && this.canEditSettings;
        },
    },
    watch: {
        /**
         * A cid switch invalidates everything, but only the section on screen is
         * re-fetched now — the other reloads when it is next opened.
         */
        cid() {
            this.loadedTabs = {};
            this.loadActiveTab();
        },
        /** Covers back/forward between sections as well as clicking the rail. */
        activeTab: {
            immediate: true,
            handler() {
                this.loadActiveTab();
            },
        },
        /**
         * The text is the source of truth, so re-derive the parsed document and
         * the error list from it — whichever mode produced the change.
         */
        contextDraft: {
            immediate: true,
            handler(text) {
                this.revalidateContext(text);
            },
        },
    },
    created() {
        // Make the default explicit in the URL so the address bar is always
        // shareable, including on a bare /paice/settings link. `replace` keeps
        // it out of history — it is a normalization, not a navigation.
        if (this.$route.query.tab !== this.activeTab) {
            this.$router.replace({
                query: { ...this.$route.query, tab: this.activeTab },
            }).catch(() => { });
        }
    },
    methods: {
        selectTab(name) {
            if (name === this.activeTab) return;
            this.$router.push({
                query: { ...this.$route.query, tab: name },
            }).catch(() => { });
        },

        /**
         * Fetch the section in view, once, unless a refresh forces it.
         *
         * The tab is marked loaded up front, so a failed load is not retried on
         * every switch back — the error state stays put and Refresh is the way
         * out of it. A fetch already in flight is never doubled up.
         */
        loadActiveTab({ force = false } = {}) {
            const tab = this.activeTab;
            // The MCP Servers panel fetches for itself; Refresh just nudges it.
            if (tab === 'servers') {
                if (force && !this.serversLoading) this.serversReloadToken += 1;
                return undefined;
            }
            const isContext = tab === 'context';
            if (isContext ? this.contextLoading : this.loading) return;
            if (!force && this.loadedTabs[tab]) return;
            this.loadedTabs = { ...this.loadedTabs, [tab]: true };
            return isContext ? this.loadContext() : this.load();
        },

        /** Refresh re-fetches only what the current tab shows. */
        refresh() {
            return this.loadActiveTab({ force: true });
        },

        DateTimeFormat,
        ...mapActions(['showModal', 'hideModal']),
        ...mapActions('paice', ['getPaiceSettings', 'getAvailableModels', 'savePaiceSettings', 'deletePaiceSettings',
            'getContextLayer', 'saveContextLayer']),

        /** Drives the unsaved-changes dot on each nav item. */
        tabIsDirty(name) {
            if (name === 'agent') return this.isDirty;
            if (name === 'context') return this.contextDirty;
            return false;
        },

        async load() {
            if (!this.cid) return;
            this.loading = true;
            this.loadError = "";
            this.saveError = "";
            // The catalog only feeds the picker, so a failure there must not block
            // the page — free-text model entry still works without it.
            this.getAvailableModels().catch(() => { });
            try {
                await this.getPaiceSettings();
                this.syncFromServer();
            } catch (e) {
                this.loadError = this.errorMessage(e, "Could not load settings.");
                this.$store.dispatch('addToast', {
                    type: 'error',
                    message: 'Something went wrong while fetching settings. Please contact support',
                });
            } finally {
                this.loading = false;
            }
        },

        /**
         * Rebuild draft + baseline from the store. `source` tells us whether the
         * resolved modelId is the client's own or inherited, so an inherited one
         * is shown as "no override" rather than pinned into the draft.
         */
        syncFromServer() {
            const s = this.settings || {};
            const ownModel = s.source?.modelId === 'client' ? (s.modelId || "") : "";
            const next = {
                systemPrompt: s.systemPrompt || "",
                modelId: ownModel,
            };
            this.draft = { ...next };
            this.baseline = { ...next };
        },

        discard() {
            this.draft = { ...this.baseline };
            this.saveError = "";
        },

        /** A concurrent edit by another admin is overwritten rather than surfaced. */
        async save() {
            if (!this.canSaveAgentSetting) return;
            this.saving = true;
            this.saveError = "";
            try {
                await this.savePaiceSettings({
                    systemPrompt: this.draft.systemPrompt.trim(),
                    modelId: this.draft.modelId,
                });
                this.syncFromServer();
                this.$store.dispatch('addToast', {
                    type: 'success',
                    message: 'PAICE settings saved successfully',
                });
            } catch (e) {
                this.saveError = this.errorMessage(e, "Could not save settings.");
            } finally {
                this.saving = false;
            }
        },

        /**
         * Ask before deleting the client's stored agent settings.
         *
         * The dialog is built from what is *stored*, not from the draft: the
         * question is what the reset takes away, and an unsaved edit is not
         * part of that — it gets its own line only because it is discarded too.
         */
        resetAll() {
            const storedPrompt = (this.settings?.systemPrompt || '').trim();
            const storedModel = this.settings?.source?.modelId === 'client'
                ? (this.settings.modelId || '')
                : '';

            const facts = [{
                label: 'System prompt',
                value: storedPrompt
                    ? `${storedPrompt.length.toLocaleString()} characters`
                    : 'Not set',
            }];
            facts.push({
                label: 'Model',
                value: storedModel || 'Service default',
                mono: !!storedModel,
            });

            const points = [];
            if (storedPrompt) {
                points.push({
                    icon: 'fa-list',
                    tone: 'danger',
                    text: 'The system prompt is deleted — there is no copy of it here '
                        + 'afterwards.',
                });
            }
            if (storedModel) {
                points.push({
                    icon: 'fa-brain',
                    tone: 'danger',
                    text: `The model override (${storedModel}) is removed.`,
                });
            }
            points.push({
                icon: 'fa-comments',
                text: 'Chats fall back to the service default prompt and model from '
                    + 'their next message.',
            });
            if (this.isDirty) {
                points.push({
                    icon: 'fa-eraser',
                    tone: 'danger',
                    text: 'Your unsaved edits go with them.',
                });
            }

            this.showModal({
                component: 'PaiceConfirm',
                data: {
                    tone: 'danger',
                    title: 'Reset PAICE settings to defaults?',
                    lede: `The prompt and model saved for ${this.cid} will be removed, `
                        + 'and PAICE goes back to service defaults.',
                    facts,
                    shift: {
                        from: { tone: 'ready', icon: 'fa-user-pen', label: 'Client settings' },
                        to: { tone: 'off', icon: 'fa-gears', label: 'Service defaults' },
                    },
                    points,
                    irreversible: true,
                    confirmText: 'Reset to defaults',
                    // PaiceConfirm dismisses itself once this settles, so it has
                    // to resolve either way — performReset reports its own.
                    confirm: () => this.performReset(),
                },
            });
        },

        async performReset() {
            this.saving = true;
            this.saveError = "";
            try {
                await this.deletePaiceSettings();
                this.syncFromServer();
                this.$store.dispatch('addToast', {
                    type: 'success',
                    message: 'Settings reverted to service defaults',
                });
            } catch (e) {
                if (e.response?.status === 404) {
                    // Nothing stored — already at defaults.
                    await this.load();
                } else {
                    this.saveError = this.errorMessage(e, "Could not reset settings.");
                }
            } finally {
                this.saving = false;
            }
        },

        // --- Context layer ---

        async loadContext() {
            if (!this.cid) return;
            this.contextLoading = true;
            this.contextLoadError = "";
            this.contextSaveError = "";
            this.contextStarted = false;
            this.contextMode = 'yaml';
            try {
                // Resolves to "" when this cid has never authored one — an empty
                // state rather than a failure.
                const text = await this.getContextLayer();
                this.contextDraft = text || "";
                this.contextBaseline = this.contextDraft;
            } catch (e) {
                this.contextLoadError = this.errorMessage(e, "Could not load the context layer.");
                this.$store.dispatch('addToast', {
                    type: 'error',
                    message: 'Something went wrong while fetching the context layer. Please contact support',
                });
            } finally {
                this.contextLoading = false;
            }
        },

        /**
         * Re-parse and re-validate the draft.
         *
         * `contextDoc` is only replaced when the text actually parses, so a
         * transient syntax error while typing in YAML mode does not tear down
         * the builder's state behind the tab.
         */
        revalidateContext(text) {
            this.showAllErrors = false;

            if (!text || !text.trim()) {
                // An empty document is only an error once the user has committed
                // to authoring one; before that it is just the empty state.
                this.contextDoc = null;
                this.contextErrors = this.contextStarted
                    ? [{ path: '<root>', message: 'the context layer is empty' }]
                    : [];
                this.contextSyntaxError = false;
                return;
            }

            const { data, errors, syntaxError } = parseAndValidate(text);
            this.contextErrors = errors;
            this.contextSyntaxError = syntaxError;
            if (!syntaxError) this.contextDoc = data;
        },

        /**
         * Switching is always allowed. An incomplete or invalid document is the
         * normal state while authoring — a half-filled domain added in Easy mode
         * makes the document invalid, and refusing to re-enter Easy mode would
         * lock the user out of the view they were using to finish it. Schema
         * errors ride along in the alert below the editor instead.
         */
        switchMode(mode) {
            if (mode === this.contextMode) return;
            // While typing in YAML mode a stale `contextDoc` is kept so a
            // momentary syntax error doesn't tear down the form behind the tab.
            // On the way *into* the form that staleness would be a trap — the
            // user would edit a phantom document and their first keystroke would
            // overwrite the very YAML they were fixing. So drop it here and let
            // the unparseable fallback render instead.
            if (mode === 'easy' && this.contextSyntaxError) this.contextDoc = null;
            this.contextMode = mode;
        },

        /**
         * A builder edit re-serializes the whole document back into the draft,
         * keeping the text authoritative.
         *
         * Comments are grafted back on from the draft as it stood *before* this
         * edit, so they survive Easy mode. Passing the current draft rather than
         * a snapshot taken on entry matters twice over: successive keystrokes
         * chain (each edit re-reads the comments the previous one preserved),
         * and a comment typed in YAML mode between two Easy mode edits is picked
         * up rather than reverted.
         *
         * Hand-authored *formatting* is still normalized — see
         * `stringifyPreservingComments`.
         */
        onBuilderChange(doc) {
            this.contextDoc = doc;
            this.contextDraft = stringifyPreservingComments(doc, this.contextDraft);
        },

        startFromTemplate() {
            this.contextStarted = true;
            this.contextDraft = STARTER_TEMPLATE;
            this.contextMode = 'yaml';
        },

        startBlank() {
            this.contextStarted = true;
            this.contextDraft = stringifyContextLayer(blankLayer());
            // Straight into the form: a blank document is exactly the case where
            // the field-by-field view beats an empty text buffer.
            this.contextMode = 'easy';
        },

        discardContext() {
            this.contextDraft = this.contextBaseline;
            this.contextSaveError = "";
            if (!this.contextBaseline.trim()) this.contextStarted = false;
        },

        async saveContext() {
            if (!this.canSaveContext) return;
            this.contextSaving = true;
            this.contextSaveError = "";
            try {
                await this.saveContextLayer(this.contextDraft);
                this.contextBaseline = this.contextDraft;
                this.$store.dispatch('addToast', {
                    type: 'success',
                    message: 'Context layer saved successfully',
                });
            } catch (e) {
                const status = e.response?.status;
                if (status === 400) {
                    // The service validates more strictly than we can; surface
                    // its per-field errors in the same list as our own.
                    const serverErrors = normalizeServerErrors(e.response?.data?.detail);
                    if (serverErrors.length) {
                        this.contextErrors = serverErrors;
                        this.contextSaveError = "The service rejected this context layer. See the issues above.";
                    } else {
                        this.contextSaveError = this.errorMessage(e, "The context layer is not valid.");
                    }
                } else if (status === 403) {
                    this.contextSaveError = "You do not have permission to change the context layer for this client.";
                } else if (status === 413) {
                    this.contextSaveError = `The context layer is too large — the limit is ${this.maxContextMB} MB.`;
                } else {
                    this.contextSaveError = this.errorMessage(e, "Could not save the context layer.");
                }
            } finally {
                this.contextSaving = false;
            }
        },

        errorMessage(e, fallback) {
            const detail = e?.response?.data?.detail;
            // The agents service returns structured detail objects for validation
            // failures; those are rendered as a list elsewhere, so avoid
            // stringifying one into "[object Object]" here.
            if (detail && typeof detail === 'object') {
                return detail.error || detail.message || fallback;
            }
            return detail || e?.response?.data?.error || e?.message || fallback;
        },
    },
};
</script>

<style scoped>
.settings-layout {
    gap: 0;
}

/* Tab rail — cards rather than list rows, so each section reads as a
   destination with its own summary instead of a bare label. */
.settings-nav {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    position: sticky;
    top: 1rem;
}

.settings-nav-label {
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #95aac9;
    padding: 0 0.25rem;
    margin-bottom: 0.2rem;
}

.settings-nav-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.7rem;
    width: 100%;
    text-align: left;
    padding: 0.7rem 0.8rem;
    border: 1px solid #edf2f9;
    border-radius: 0.5rem;
    background: #fff;
    color: #6e84a3;
    overflow: hidden;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

/* The active marker is a bar rather than a border so the card's geometry does
   not shift between states. */
.settings-nav-item::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.55rem;
    bottom: 0.55rem;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: transparent;
    transition: background-color 0.15s ease;
}

.settings-nav-icon {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 0.45rem;
    background: #f1f4f9;
    color: #6e84a3;
    transition: background-color 0.15s ease, color 0.15s ease;
}

.settings-nav-icon i {
    font-size: 0.8rem;
}

.settings-nav-text {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
}

.settings-nav-title {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.82rem;
    font-weight: 600;
    color: #12263f;
    line-height: 1.3;
}

.settings-nav-desc {
    font-size: 0.7rem;
    color: #95aac9;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.settings-nav-chevron {
    margin-left: auto;
    font-size: 0.65rem;
    color: #d2ddec;
    opacity: 0;
    transform: translateX(-0.2rem);
    transition: opacity 0.15s ease, transform 0.15s ease;
}

.settings-nav-item:hover {
    border-color: #d2ddec;
    box-shadow: 0 2px 6px rgba(18, 38, 63, 0.06);
}

.settings-nav-item:hover .settings-nav-chevron {
    opacity: 1;
    transform: translateX(0);
}

.settings-nav-item.active {
    border-color: #b6d4fb;
    background: #f4f9ff;
    box-shadow: 0 2px 8px rgba(44, 123, 229, 0.1);
}

.settings-nav-item.active::before {
    background: #2c7be5;
}

.settings-nav-item.active .settings-nav-icon {
    background: #2c7be5;
    color: #fff;
}

.settings-nav-item.active .settings-nav-title {
    color: #2c7be5;
}

.settings-nav-item.active .settings-nav-chevron {
    opacity: 1;
    transform: translateX(0);
    color: #2c7be5;
}

/* Marks a section as in-progress. Sits inside the title so it reads as part
   of the label rather than as status about the section's contents. */
.settings-nav-badge {
    flex-shrink: 0;
    font-size: 0.58rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    line-height: 1;
    margin-left: 0.15rem;
    padding: 0.2rem 0.35rem;
    border-radius: 0.25rem;
    background: #fdf0e0;
    color: #a76420;
}

/* .settings-nav-item.active .settings-nav-badge {
    background: #fff;
    color: #a76420;
} */

.settings-nav-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #d9822b;
    flex-shrink: 0;
}

/* Card
   The theme pins `.card-header` to a fixed 60px (`$card-header-height`), which
   assumes a single-line title. These headers carry a subtitle and a right-hand
   meta line, so on narrow panels — or at browser zoom — the subtitle wraps and
   the second line spills out under the header's bottom border. Let the header
   grow instead and keep 60px only as the floor, so single-line headers still
   line up with the rest of the app. */
.card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem 1rem;
    flex-wrap: wrap;
    height: auto;
    min-height: 60px;
}

/* The theme gives the first child `flex: 1` but not `min-width: 0`, so a long
   unbreakable string (a cid, a URL) would push the header wider than the card. */
.card-header > *:first-child {
    min-width: 0;
}

.settings-subtitle {
    font-size: 0.75rem;
    color: #95aac9;
    margin-top: 0.15rem;
}

.updated-meta {
    font-size: 0.72rem;
}

.settings-loading {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    color: #95aac9;
    font-size: 0.85rem;
    justify-content: center;
}

.settings-field-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.6rem;
}

.settings-help {
    font-size: 0.75rem;
    color: #95aac9;
    max-width: 46rem;
    margin-top: 0.15rem;
}

.settings-divider {
    margin: 1.75rem 0;
    border-color: #edf2f9;
}

/* Provenance pill — the page's organizing signal: every field says whether it
   is the client's own value or inherited from the service. */
.source-pill {
    flex-shrink: 0;
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 0.25rem 0.55rem;
    border-radius: 999px;
    white-space: nowrap;
}

.source-pill-default {
    background: #edf2f9;
    color: #6e84a3;
}

.source-pill-custom {
    background: #e8f1fd;
    color: #2c7be5;
}

/* Prompt editor */
.editor {
    border: 1px solid #d2ddec;
    border-radius: 0.375rem;
    overflow: hidden;
}

.editor-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.4rem 0.5rem;
    background: #f9fbfd;
    border-bottom: 1px solid #edf2f9;
}

.editor-modes {
    display: flex;
    gap: 0.2rem;
}

.editor-mode {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    border: 0;
    background: transparent;
    color: #6e84a3;
    font-size: 0.75rem;
    font-weight: 500;
    padding: 0.3rem 0.65rem;
    border-radius: 0.3rem;
}

.editor-mode i {
    font-size: 0.7rem;
}

.editor-mode:hover {
    color: #12263f;
}

.editor-mode.active {
    background: #fff;
    color: #2c7be5;
    box-shadow: 0 1px 2px rgba(18, 38, 63, 0.08);
}

.editor-count {
    font-size: 0.7rem;
    color: #95aac9;
    font-variant-numeric: tabular-nums;
}

.editor-input {
    border: 0;
    border-radius: 0;
    resize: vertical;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.8rem;
    line-height: 1.6;
}

.editor-input:focus {
    box-shadow: none;
}

.editor-preview {
    padding: 0.85rem 0.9rem;
    min-height: 18rem;
    max-height: 32rem;
    overflow-y: auto;
    font-size: 0.85rem;
}

.editor-preview-empty {
    color: #95aac9;
    font-size: 0.8rem;
}

/* Resolved model readout */
.resolved-box {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    background: #f9fbfd;
    border: 1px solid #edf2f9;
    border-radius: 0.375rem;
    padding: 0.6rem 0.75rem;
}

.resolved-label {
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #95aac9;
}

.resolved-value {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.75rem;
    color: #12263f;
    word-break: break-all;
}

.resolved-note {
    font-size: 0.7rem;
    color: #95aac9;
}

.resolved-advisory {
    font-size: 0.7rem;
    line-height: 1.5;
    color: #a76420;
    margin-top: 0.3rem;
    padding-top: 0.4rem;
    border-top: 1px solid #edf2f9;
}

.revert-link {
    font-size: 0.75rem;
    margin-top: 0.3rem;
}

/* Context layer */
.context-intro {
    text-align: center;
    max-width: 34rem;
    margin: 0 auto;
    padding: 2.5rem 1rem;
}

.context-intro-icon {
    font-size: 1.75rem;
    color: #d2ddec;
    margin-bottom: 0.9rem;
    display: block;
}

.context-intro-title {
    font-size: 1rem;
    color: #12263f;
    margin-bottom: 0.4rem;
}

.context-intro-text {
    font-size: 0.8rem;
    color: #95aac9;
    margin-bottom: 1.25rem;
}

.context-intro-actions {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
}

.context-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 0.9rem;
}

/* Reuses the Write/Preview pill styling from the prompt editor so both
   toggles on this page read as the same control. */
.context-bar .editor-modes {
    background: #f9fbfd;
    border: 1px solid #edf2f9;
    border-radius: 0.375rem;
    padding: 0.15rem;
}

.context-stats {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    font-size: 0.72rem;
    color: #95aac9;
}

.context-stats strong {
    color: #12263f;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}

.context-size {
    padding-left: 0.85rem;
    border-left: 1px solid #edf2f9;
    font-variant-numeric: tabular-nums;
}

.context-alert {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    border-radius: 0.375rem;
    padding: 0.65rem 0.8rem;
    font-size: 0.78rem;
    /* Sits below the editor, reporting on it. */
    margin-top: 0.9rem;
}

.context-alert i {
    font-size: 0.8rem;
    margin-top: 0.1rem;
}

.context-alert.error {
    background: #fdeaed;
    color: #b02a3a;
}

.context-alert.ok {
    background: #e6f6ee;
    color: #24795a;
}

.context-error-list {
    list-style: none;
    padding: 0;
    margin: 0.35rem 0 0;
}

.context-error-list li {
    display: flex;
    gap: 0.4rem;
    font-size: 0.74rem;
    padding: 0.1rem 0;
}

.context-error-list code {
    font-size: 0.7rem;
    background: rgba(176, 42, 58, 0.1);
    color: inherit;
    border-radius: 0.2rem;
    padding: 0 0.25rem;
    white-space: nowrap;
    flex-shrink: 0;
}

.context-more {
    font-size: 0.73rem;
    color: inherit;
    text-decoration: underline;
}

.context-editor {
    border-radius: 0.375rem;
    overflow: hidden;
}

/* Shown in Easy mode when the YAML does not parse, so there is no document to
   lay out as a form. */
.context-unparseable {
    text-align: center;
    padding: 3rem 1rem;
    color: #95aac9;
    font-size: 0.8rem;
    background: #f9fbfd;
    border: 1px dashed #e3ebf6;
    border-radius: 0.375rem;
}

.context-unparseable i {
    font-size: 1.5rem;
    color: #d2ddec;
    margin-bottom: 0.75rem;
    display: block;
}

.context-unparseable strong {
    color: #12263f;
}

.card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
}

.footer-status {
    font-size: 0.78rem;
}

@media (max-width: 991.98px) {
    .settings-nav {
        position: static;
        flex-direction: row;
        overflow-x: auto;
        margin-bottom: 1rem;
        padding-bottom: 0.25rem;
    }

    /* The section heading only makes sense above a vertical stack. */
    .settings-nav-label {
        display: none;
    }

    .settings-nav-item {
        width: auto;
        flex: 1 0 auto;
    }

    /* Drop the summary line and chevron so the cards stay compact enough to sit
       side by side without scrolling on most phones. */
    .settings-nav-desc,
    .settings-nav-chevron {
        display: none;
    }
}
</style>

<style lang="scss">
@use '@/theme/scss/_user-variables.scss' as *;

body.dark-mode {

    .settings-nav-label,
    .settings-nav-desc {
        color: $dark-text-muted !important;
    }

    .settings-nav-item {
        background: $dark-card-bg !important;
        border-color: $dark-border-color !important;
        color: $dark-text-secondary !important;
    }

    .settings-nav-title {
        color: $dark-text-primary !important;
    }

    .settings-nav-icon {
        background: $dark-elevated-bg !important;
        color: $dark-text-secondary !important;
    }

    .settings-nav-chevron {
        color: $dark-text-muted !important;
    }

    .settings-nav-item:hover {
        border-color: lighten($dark-border-color, 8%) !important;
        box-shadow: none !important;
    }

    .settings-nav-item.active {
        background: #2a3a509f !important;
        border-color: $dark-accent !important;
        box-shadow: none !important;
    }

    .settings-nav-item.active::before {
        background: $dark-accent !important;
    }

    .settings-nav-item.active .settings-nav-icon {
        background: $dark-accent !important;
        color: #fff !important;
    }

    .settings-nav-item.active .settings-nav-title,
    .settings-nav-item.active .settings-nav-chevron {
        color: #7ab0ff !important;
    }

    .settings-nav-badge,
    .settings-nav-item.active .settings-nav-badge {
        background: rgba(224, 164, 88, 0.16) !important;
        color: #e0a458 !important;
    }

    .settings-nav {
        border-color: $dark-border-color !important;
    }

    .settings-subtitle,
    .settings-help,
    .editor-count,
    .resolved-label,
    .resolved-note,
    .editor-preview-empty {
        color: $dark-text-muted !important;
    }

    .settings-divider {
        border-color: $dark-border-color !important;
    }

    .source-pill-default {
        background: $dark-elevated-bg !important;
        color: $dark-text-secondary !important;
    }

    .source-pill-custom {
        background: $dark-selected-bg !important;
        color: #7ab0ff !important;
    }

    .editor {
        border-color: $dark-border-color !important;
    }

    .editor-toolbar {
        background: $dark-body-bg !important;
        border-color: $dark-border-color !important;
    }

    .editor-mode {
        color: $dark-text-secondary !important;
    }

    .editor-mode.active {
        background: $dark-input-bg !important;
        color: #7ab0ff !important;
    }

    .editor-input {
        background-color: $dark-input-bg !important;
        color: $dark-text-primary !important;
    }

    .editor-preview {
        background: $dark-input-bg !important;
        color: $dark-text-primary !important;
    }

    .resolved-box {
        background: $dark-body-bg !important;
        border-color: $dark-border-color !important;
    }

    .resolved-value {
        color: $dark-text-primary !important;
    }

    .resolved-advisory {
        color: #e0a458 !important;
        border-top-color: $dark-border-color !important;
    }

    .context-intro-title {
        color: $dark-text-primary !important;
    }

    .context-intro-icon,
    .context-intro-text,
    .context-stats {
        color: $dark-text-muted !important;
    }

    .context-bar .editor-modes {
        background: $dark-body-bg !important;
        border-color: $dark-border-color !important;
    }

    .context-stats strong {
        color: $dark-text-primary !important;
    }

    .context-size {
        border-left-color: $dark-border-color !important;
    }

    .context-alert.error {
        background: rgba(230, 55, 87, 0.14) !important;
        color: #f08a9c !important;
    }

    .context-unparseable {
        background: $dark-body-bg !important;
        border-color: $dark-border-color !important;
        color: $dark-text-muted !important;
    }

    .context-unparseable strong {
        color: $dark-text-primary !important;
    }

    .context-alert.ok {
        background: rgba(0, 217, 126, 0.12) !important;
        color: #5fd3a3 !important;
    }

    .context-error-list code {
        background: rgba(255, 255, 255, 0.08) !important;
    }
}
</style>
